import { useTranslation } from "react-i18next"
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom"

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
interface AppPaginationProps {
  pagination: {
    total: number
    currentPage: number
    totalPages: number
  }
  startPage?: number
}
export const AppPagination = ({
  pagination,
  startPage = 1,
}: AppPaginationProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const adjustedCurrentPage =
    pagination.currentPage - startPage + 1
  const adjustedTotalPages = pagination.totalPages
  const selectedSize = Number(
    searchParams.get("pageSize") || 10
  )
  const createPageURL = (pageNumber: number) => {
    const params = new URLSearchParams(searchParams)
    const adjustedPageNumber = pageNumber + startPage - 1
    if (adjustedPageNumber === startPage) {
      params.delete("page")
    } else {
      params.set("page", adjustedPageNumber.toString())
    }
    return `?${params.toString()}`
  }
  const handleSelectPageSize = (value: string) => {
    const params = new URLSearchParams(searchParams)
    params.set("pageSize", value)
    params.set(
      "page",
      startPage ? startPage.toString() : "1"
    )
    navigate(`?${params.toString()}`)
  }
  return (
    <div className="flex items-center justify-between gap-4 max-sm:flex-col">
      <div className="flex flex-1 flex-col gap-1 whitespace-nowrap text-sm">
        <p
          className="text-muted-foreground"
          aria-live="polite"
        >
          {t("pagination.page")}{" "}
          <span className="text-foreground">
            {pagination.total > 0 ? adjustedCurrentPage : 0}
          </span>{" "}
          {t("pagination.of")}{" "}
          <span className="text-foreground">
            {adjustedTotalPages}
          </span>
        </p>
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            {pagination.total}
          </span>{" "}
          {pagination.total === 1
            ? t("pagination.recordFound")
            : t("pagination.recordsFound")}
        </p>
      </div>
      <Pagination>
        <PaginationContent>
          {adjustedCurrentPage !== 1 && (
            <PaginationItem>
              <PaginationPrevious
                to={createPageURL(adjustedCurrentPage - 1)}
                className={cn("cursor-pointer")}
              />
            </PaginationItem>
          )}
          {Array.from(
            { length: adjustedTotalPages },
            (_, i) => i + 1
          ).map((page) => {
            if (
              page === 1 ||
              page === adjustedTotalPages ||
              (page >= adjustedCurrentPage - 1 &&
                page <= adjustedCurrentPage + 1)
            ) {
              return (
                <PaginationItem key={page}>
                  <PaginationLink
                    to={createPageURL(page)}
                    isActive={page === adjustedCurrentPage}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              )
            } else if (
              page === adjustedCurrentPage - 2 ||
              page === adjustedCurrentPage + 2
            ) {
              return (
                <PaginationItem key={page}>
                  <PaginationEllipsis />
                </PaginationItem>
              )
            }
            return null
          })}
          {adjustedCurrentPage !== adjustedTotalPages &&
            pagination.total > 0 && (
              <PaginationItem>
                <PaginationNext
                  to={createPageURL(
                    adjustedCurrentPage + 1
                  )}
                  className={cn("cursor-pointer")}
                />
              </PaginationItem>
            )}
        </PaginationContent>
      </Pagination>
      <div>
        <div className="flex flex-1 justify-end">
          <Select
            value={selectedSize.toString()}
            onValueChange={handleSelectPageSize}
            aria-label="Resultados por página"
          >
            <SelectTrigger
              id="results-per-page"
              className="w-fit whitespace-nowrap"
            >
              <SelectValue placeholder="Selecione o número de resultados" />
            </SelectTrigger>
            <SelectContent>
              {[5, 10, 25, 50].map((pageSize) => (
                <SelectItem
                  key={pageSize}
                  value={pageSize.toString()}
                >
                  {pageSize} {t("pagination.perPage")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  )
}
